import {
  serviceCategories,
  getCategoryBySlug,
  type Service,
  type ServiceCategory,
} from "@/data/servicesData";
import type { SiteLanguageCode } from "@/lib/language";

type ServiceText = {
  title: string;
  description: string;
  longDescription: string;
  features: string[];
  tags?: string[];
};

type CategoryText = {
  title: string;
  description: string;
  longDescription: string;
  services: Record<string, ServiceText>;
};

/** Hindi overlays keyed by English category record key (webApp, aiAutomation, …). */
const servicesHi: Record<string, CategoryText> = {
  webApp: {
    title: "वेब और ऐप डेवलपमेंट",
    description:
      "आधुनिक तकनीकों से बने स्केलेबल एप्लिकेशन",
    longDescription:
      "हम नवीनतम तकनीकों से उत्कृष्ट डिजिटल अनुभव बनाते हैं। रेस्पॉन्सिव वेबसाइट से लेकर जटिल वेब एप्लिकेशन तक - ऐसे समाधान जो व्यवसाय की वृद्धि और उपयोगकर्ता जुड़ाव बढ़ाएँ।",
    services: {
      "web-development": {
        title: "वेब डेवलपमेंट",
        description:
          "React, Next.js और आधुनिक फ्रेमवर्क से उच्च-परफ़ॉर्मेंस वेबसाइटें।",
        longDescription:
          "हम तेज़, SEO-अनुकूलित वेबसाइटें बनाते हैं जो उपयोगकर्ताओं को आकर्षित करें और कन्वर्ज़न बढ़ाएँ। आधुनिक टेक स्टैक आपके साइट को आगे रखता है।",
        features: [
          "सभी डिवाइस के लिए रेस्पॉन्सिव डिज़ाइन",
          "अंतर्निहित SEO अनुकूलन",
          "परफ़ॉर्मेंस-प्रथम आर्किटेक्चर",
          "आधुनिक UI/UX पैटर्न",
          "एक्सेसिबिलिटी अनुपालन",
        ],
      },
      "full-stack-web-apps": {
        title: "फुल स्टैक वेब ऐप्स",
        description:
          "मज़बूत बैकएंड और सहज फ्रंटएंड वाले एंड-टू-एंड समाधान।",
        longDescription:
          "डेटाबेस डिज़ाइन से यूज़र इंटरफ़ेस तक पूरा वेब एप्लिकेशन डेवलपमेंट। पूरा स्टैक हम संभालते हैं - आप अपने व्यवसाय पर ध्यान दें।",
        features: [
          "स्केलेबल आर्किटेक्चर डिज़ाइन",
          "रियल-टाइम डेटा सिंक",
          "सुरक्षित ऑथेंटिकेशन सिस्टम",
          "डेटाबेस अनुकूलन",
          "API-प्रथम डेवलपमेंट",
        ],
      },
      "website-customization": {
        title: "वेबसाइट कस्टमाइज़ेशन",
        description:
          "आपके ब्रांड और व्यावसायिक ज़रूरतों के अनुरूप अनुकूलन।",
        longDescription:
          "कस्टम फीचर्स, डिज़ाइन अपडेट और कार्यक्षमता सुधारों से मौजूदा वेबसाइट को बदलें - आपके बदलते व्यावसायिक लक्ष्यों के साथ।",
        features: [
          "कस्टम थीम डेवलपमेंट",
          "प्लगइन और एक्सटेंशन डेवलपमेंट",
          "परफ़ॉर्मेंस सुधार",
          "ब्रांड आइडेंटिटी इंटीग्रेशन",
          "कंटेंट मैनेजमेंट समाधान",
        ],
      },
      "android-app-development": {
        title: "एंड्रॉइड ऐप डेवलपमेंट",
        description:
          "Kotlin के साथ नेटिव एंड्रॉइड ऐप्स और क्रॉस-प्लेटफ़ॉर्म समाधान।",
        longDescription:
          "एंड्रॉइड उपयोगकर्ताओं के लिए शक्तिशाली मोबाइल अनुभव। नेटिव Kotlin से Flutter तक - ऐसे ऐप्स जिन्हें उपयोगकर्ता पसंद करें।",
        features: [
          "नेटिव एंड्रॉइड डेवलपमेंट",
          "क्रॉस-प्लेटफ़ॉर्म समाधान",
          "Material Design लागू करना",
          "ऑफ़लाइन-प्रथम क्षमताएँ",
          "पुश नोटिफ़िकेशन और एनालिटिक्स",
        ],
      },
      "apis-integrations": {
        title: "API और इंटीग्रेशन",
        description:
          "सहज थर्ड-पार्टी इंटीग्रेशन और कस्टम API डेवलपमेंट।",
        longDescription:
          "शक्तिशाली API और सहज इंटीग्रेशन से अपने सिस्टम जोड़ें। मज़बूत बैकएंड सेवाएँ जो एप्लिकेशन को कुशलता से संवाद कराएँ।",
        features: [
          "RESTful API डिज़ाइन",
          "GraphQL इम्प्लीमेंटेशन",
          "थर्ड-पार्टी इंटीग्रेशन",
          "वेबहुक सिस्टम",
          "API डॉक्यूमेंटेशन",
        ],
      },
    },
  },
  aiAutomation: {
    title: "AI और ऑटोमेशन",
    description:
      "बुद्धिमान सिस्टम जो वर्कफ़्लो स्वचालित करें और व्यवसाय स्केल करें",
    longDescription:
      "कृत्रिम बुद्धिमत्ता की शक्ति से अपने संचालन को बदलें। हमारे AI समाधान जटिल कार्यों को स्वचालित करते हैं, बुद्धिमान इनसाइट देते हैं और नए अवसर खोलते हैं।",
    services: {
      "ai-ml-solutions": {
        title: "AI / ML समाधान",
        description:
          "आपके विशिष्ट उपयोग-मामलों के लिए कस्टम मशीन लर्निंग मॉडल।",
        longDescription:
          "आपकी व्यावसायिक चुनौतियों के लिए डिज़ाइन किए गए कस्टम ML मॉडल। प्रेडिक्टिव एनालिटिक्स से कंप्यूटर विज़न तक - परिणाम देने वाला AI।",
        features: [
          "कस्टम मॉडल डेवलपमेंट",
          "प्रेडिक्टिव एनालिटिक्स",
          "कंप्यूटर विज़न समाधान",
          "रिकमेंडेशन सिस्टम",
          "मॉडल ट्रेनिंग और डिप्लॉयमेंट",
        ],
      },
      "ai-technology-consulting": {
        title: "AI टेक्नोलॉजी कंसल्टिंग",
        description:
          "संगठन में AI लागू करने पर रणनीतिक मार्गदर्शन।",
        longDescription:
          "विशेषज्ञ मार्गदर्शन से AI परिदृश्य को समझें। अवसर पहचानें, रणनीति बनाएँ और व्यावसायिक उद्देश्यों से मेल खाते AI समाधान लागू करें।",
        features: [
          "AI तत्परता आकलन",
          "टेक्नोलॉजी रोडमैप प्लानिंग",
          "यूज़ केस पहचान",
          "ROI विश्लेषण",
          "इम्प्लीमेंटेशन रणनीति",
        ],
      },
      "ai-agents": {
        title: "AI एजेंट्स",
        description:
          "जटिल कार्यों और निर्णय लेने वाले स्वायत्त एजेंट।",
        longDescription:
          "बुद्धिमान AI एजेंट डिप्लॉय करें जो स्वायत्त रूप से जटिल कार्य पूरे करें। कस्टमर सर्विस से डेटा विश्लेषण तक - एजेंट लगातार सीखते और सुधरते हैं।",
        features: [
          "स्वायत्त कार्य निष्पादन",
          "मल्टी-एजेंट ऑर्केस्ट्रेशन",
          "निर्णय-निर्माण सिस्टम",
          "निरंतर सीखना",
          "ह्यूमन-इन-द-लूप विकल्प",
        ],
      },
      "ai-fine-tuning": {
        title: "AI फाइन-ट्यूनिंग",
        description:
          "डोमेन-विशिष्ट ज़रूरतों के लिए प्री-ट्रेंड मॉडल अनुकूलित करें।",
        longDescription:
          "अपने डोमेन के लिए शक्तिशाली AI मॉडल कस्टमाइज़ करें। फाइन-ट्यूनिंग बेहतर परफ़ॉर्मेंस, कम लागत और अधिक सटीक परिणाम देती है।",
        features: [
          "डोमेन-विशिष्ट ट्रेनिंग",
          "परफ़ॉर्मेंस अनुकूलन",
          "लागत घटाने की रणनीतियाँ",
          "क्वालिटी एश्योरेंस",
          "डिप्लॉयमेंट पाइपलाइन",
        ],
      },
      "ai-chatbot": {
        title: "AI चैटबॉट",
        description:
          "GPT और उन्नत भाषा मॉडल से संचालित संवादात्मक AI।",
        longDescription:
          "अत्याधुनिक भाषा मॉडल से बुद्धिमान संवादात्मक अनुभव बनाएँ। हमारे AI चैटबॉट संदर्भ समझते हैं, जटिल प्रश्न संभालते हैं और मानव-जैसी बातचीत देते हैं।",
        features: [
          "प्राकृतिक भाषा समझ",
          "संदर्भ-जागरूक उत्तर",
          "बहुभाषी समर्थन",
          "नॉलेज बेस इंटीग्रेशन",
          "एनालिटिक्स और इनसाइट्स",
        ],
      },
      "rules-based-chatbot": {
        title: "नियम-आधारित चैटबॉट",
        description:
          "पूर्वानुमेय ग्राहक बातचीत के लिए संरचित संवादात्मक फ्लो।",
        longDescription:
          "नियम-आधारित सिस्टम से विश्वसनीय चैटबॉट बनाएँ। FAQ, गाइडेड वर्कफ़्लो और संरचित ग्राहक बातचीत के लिए आदर्श।",
        features: [
          "पूर्वानुमेय उत्तर",
          "आसान मेंटनेंस",
          "स्पष्ट बातचीत फ्लो",
          "इंटीग्रेशन के लिए तैयार",
          "फ़ॉलबैक हैंडलिंग",
        ],
      },
      "automations-agents": {
        title: "ऑटोमेशन और एजेंट्स",
        description:
          "दोहराए जाने वाले कार्यों को स्केल पर समाप्त करने वाला वर्कफ़्लो ऑटोमेशन।",
        longDescription:
          "बुद्धिमान वर्कफ़्लो से व्यावसायिक प्रक्रियाओं को स्वचालित करें। सरल टास्क ऑटोमेशन से जटिल मल्टी-स्टेप प्रक्रियाओं तक - स्मार्ट तरीके से काम करें।",
        features: [
          "प्रोसेस ऑटोमेशन",
          "वर्कफ़्लो ऑर्केस्ट्रेशन",
          "इंटीग्रेशन कनेक्टर",
          "एरर हैंडलिंग",
          "मॉनिटरिंग और अलर्ट",
        ],
      },
      nlp: {
        title: "NLP",
        description:
          "टेक्स्ट विश्लेषण और समझ के लिए प्राकृतिक भाषा प्रसंस्करण समाधान।",
        longDescription:
          "उन्नत NLP तकनीकों से टेक्स्ट डेटा से इनसाइट निकालें। सेंटीमेंट एनालिसिस से एंटिटी एक्सट्रैक्शन तक - असंरचित टेक्स्ट को समझें और उपयोग करें।",
        features: [
          "सेंटीमेंट एनालिसिस",
          "एंटिटी रिकग्निशन",
          "टेक्स्ट क्लासिफिकेशन",
          "भाषा अनुवाद",
          "डॉक्यूमेंट सारांश",
        ],
      },
    },
  },
  devopsCloud: {
    title: "DevOps और क्लाउड",
    description: "स्केलेबल इन्फ्रास्ट्रक्चर और सुव्यवस्थित डिप्लॉयमेंट पाइपलाइन",
    longDescription:
      "आधुनिक DevOps प्रथाओं से मज़बूत, स्केलेबल इन्फ्रास्ट्रक्चर बनाएँ। तेज़ डिप्लॉय, सहज स्केलिंग और पूरे स्टैक में विश्वसनीयता।",
    services: {
      "devops-cloud-services": {
        title: "DevOps और क्लाउड",
        description:
          "CI/CD और इन्फ्रास्ट्रक्चर ऐज़ कोड के साथ एंड-टू-एंड क्लाउड आर्किटेक्चर।",
        longDescription:
          "क्लाउड-नेटिव आर्किटेक्चर के साथ आधुनिक DevOps लागू करें। CI/CD पाइपलाइन से कंटेनर ऑर्केस्ट्रेशन तक - स्केल करने वाला इन्फ्रा।",
        features: [
          "क्लाउड आर्किटेक्चर डिज़ाइन",
          "CI/CD पाइपलाइन सेटअप",
          "इन्फ्रास्ट्रक्चर ऐज़ कोड",
          "कंटेनर ऑर्केस्ट्रेशन",
          "मॉनिटरिंग और लॉगिंग",
        ],
      },
      "speed-optimization": {
        title: "स्पीड ऑप्टिमाइज़ेशन",
        description:
          "तेज़ लोड टाइम और रिस्पॉन्स के लिए परफ़ॉर्मेंस ट्यूनिंग।",
        longDescription:
          "पीक परफ़ॉर्मेंस के लिए एप्लिकेशन अनुकूलित करें। बॉटलनेक विश्लेषण, कैशिंग रणनीतियाँ और स्पीड के लिए इन्फ्रा फाइन-ट्यून।",
        features: [
          "परफ़ॉर्मेंस ऑडिटिंग",
          "CDN इम्प्लीमेंटेशन",
          "कैशिंग रणनीतियाँ",
          "डेटाबेस अनुकूलन",
          "कोड अनुकूलन",
        ],
      },
    },
  },
  support: {
    title: "सपोर्ट और मेंटनेंस",
    description:
      "सिस्टम सुचारू चलाने के लिए विश्वसनीय निरंतर सपोर्ट",
    longDescription:
      "व्यापक सपोर्ट सेवाओं से सिस्टम को पीक परफ़ॉर्मेंस पर रखें। प्रोएक्टिव मेंटनेंस, तेज़ इश्यू रिज़ॉल्यूशन और निरंतर सुधार।",
    services: {
      "maintenance-support": {
        title: "मेंटनेंस और सपोर्ट",
        description:
          "मन की शांति के लिए 24/7 मॉनिटरिंग और प्रोएक्टिव मेंटनेंस।",
        longDescription:
          "व्यापक मेंटनेंस और सपोर्ट सेवाओं से निश्चिंत रहें। हम चौबीसों घंटे मॉनिटर करते हैं और व्यवसाय प्रभावित होने से पहले मुद्दे हल करते हैं।",
        features: [
          "24/7 सिस्टम मॉनिटरिंग",
          "प्रोएक्टिव मेंटनेंस",
          "सिक्योरिटी अपडेट",
          "परफ़ॉर्मेंस मॉनिटरिंग",
          "समर्पित सपोर्ट टीम",
        ],
      },
      "bug-fixes": {
        title: "बग फिक्स",
        description:
          "सॉफ़्टवेयर समस्याओं और दोषों का तेज़ निदान और समाधान।",
        longDescription:
          "एप्लिकेशन सुचारू रखने के लिए तेज़ और गहन बग रिज़ॉल्यूशन। अनुभवी टीम जल्दी निदान करती है और स्थायी समाधान लागू करती है।",
        features: [
          "तेज़ निदान",
          "मूल कारण विश्लेषण",
          "गहन टेस्टिंग",
          "डॉक्यूमेंटेशन",
          "रोकथाम रणनीतियाँ",
        ],
      },
      "website-customization-support": {
        title: "वेबसाइट कस्टमाइज़ेशन",
        description:
          "प्लेटफ़ॉर्म में निरंतर सुधार और फीचर जोड़।",
        longDescription:
          "निरंतर कस्टमाइज़ेशन सेवाओं से वेबसाइट ताज़ा और प्रतिस्पर्धी रखें। नए फीचर्स, डिज़ाइन अपडेट और कार्यक्षमता सुधार।",
        features: [
          "फीचर जोड़",
          "डिज़ाइन अपडेट",
          "UX सुधार",
          "कंटेंट अपडेट",
          "A/B टेस्टिंग",
        ],
      },
      "performance-optimization": {
        title: "परफ़ॉर्मेंस ऑप्टिमाइज़ेशन",
        description:
          "निरंतर परफ़ॉर्मेंस मॉनिटरिंग और अनुकूलन सेवाएँ।",
        longDescription:
          "एप्लिकेशन तेज़ और कुशल रखने के लिए निरंतर परफ़ॉर्मेंस अनुकूलन। हम मेट्रिक्स मॉनिटर, विश्लेषण और सुधार करते हैं।",
        features: [
          "निरंतर मॉनिटरिंग",
          "परफ़ॉर्मेंस एनालिटिक्स",
          "अनुकूलन सुझाव",
          "इम्प्लीमेंटेशन सपोर्ट",
          "नियमित रिपोर्टिंग",
        ],
      },
    },
  },
};

function mergeService(service: Service, overlay?: ServiceText): Service {
  if (!overlay) return service;
  return {
    ...service,
    title: overlay.title,
    description: overlay.description,
    longDescription: overlay.longDescription,
    features: overlay.features,
    ...(overlay.tags ? { tags: overlay.tags } : {}),
  };
}

export function localizeServiceCategory(
  category: ServiceCategory,
  lang: SiteLanguageCode,
): ServiceCategory {
  if (lang !== "hi") return category;

  const key = Object.keys(serviceCategories).find(
    (k) => serviceCategories[k].id === category.id,
  );
  const overlay = key ? servicesHi[key] : undefined;
  if (!overlay) return category;

  return {
    ...category,
    title: overlay.title,
    description: overlay.description,
    longDescription: overlay.longDescription,
    services: category.services.map((service) =>
      mergeService(service, overlay.services[service.id]),
    ),
  };
}

export function localizeServiceCategories(
  lang: SiteLanguageCode,
): Record<string, ServiceCategory> {
  if (lang !== "hi") return serviceCategories;

  const result: Record<string, ServiceCategory> = {};
  for (const [key, category] of Object.entries(serviceCategories)) {
    result[key] = localizeServiceCategory(category, lang);
  }
  return result;
}

export function getLocalizedCategoryBySlug(
  slug: string,
  lang: SiteLanguageCode,
): ServiceCategory | undefined {
  const category = getCategoryBySlug(slug);
  if (!category) return undefined;
  return localizeServiceCategory(category, lang);
}

export function getLocalizedAllCategories(
  lang: SiteLanguageCode,
): ServiceCategory[] {
  return Object.values(localizeServiceCategories(lang));
}
